// Вкладка «Чаты»: диалоги поддержки, по одному на пользователя. Слева список
// (кто, последнее сообщение, непрочитанные), справа переписка. Новые сообщения
// выбранного диалога дозагружаются лёгким поллингом, пока вкладка открыта.
// Разговор может начать и админ: «Новый диалог» ищет пользователя по почте.
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useI18n, useT } from '../i18n';
import { errorKey } from '../auth';
import type { ApiError } from '../auth';
import type { SupportMessage } from '../support/api';
import { MAX_SUPPORT_FILE_BYTES, MAX_SUPPORT_TEXT, SUPPORT_FILE_ACCEPT } from '../support/api';
import { closeChat, deleteChat, fetchChat, listChats, listUsers, patchUser, sendChatMessage } from './api';
import type { AdminChat, AdminUser, ChatsPage } from './api';
import styles from './ChatsPanel.module.css';

const PAGE_SIZE = 50;
/** Пока вкладка открыта — новые сообщения выбранного диалога раз в 7 секунд. */
const POLL_MS = 7_000;

/**
 * «Chrome · macOS» из user-agent последней сессии — с какого клиента человек
 * работает. Порядок проверок важен: Chrome содержит Safari, Edge — Chrome.
 */
function shortClient(ua: string): string {
  if (!ua) return '';
  const os = /iPhone|iPad/.test(ua)
    ? 'iOS'
    : /Android/.test(ua)
      ? 'Android'
      : /Mac OS X/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : '';
  return [browser, os].filter(Boolean).join(' · ');
}

export function ChatsPanel(): ReactElement {
  const t = useT();
  const { lang } = useI18n();

  const [page, setPage] = useState<ChatsPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<ApiError | null>(null);

  const [selected, setSelected] = useState<string | null>(null);
  const [thread, setThread] = useState<{
    user: AdminUser;
    lang: string;
    userAgent: string;
    messages: SupportMessage[];
  } | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Поиск пользователя для нового диалога.
  const [starting, setStarting] = useState(false);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<AdminUser[]>([]);

  const listRef = useRef<HTMLDivElement | null>(null);
  const lastIdRef = useRef(0);

  const reloadChats = useCallback(async () => {
    const res = await listChats({ limit: PAGE_SIZE, offset });
    if (res.ok) {
      setPage(res.value);
      setError(null);
    } else {
      setError(res.error);
    }
  }, [offset]);

  useEffect(() => {
    void reloadChats();
  }, [reloadChats]);

  const scrollDown = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const merge = useCallback(
    (incoming: SupportMessage[]) => {
      if (incoming.length === 0) return;
      setThread((prev) => {
        if (!prev) return prev;
        const known = new Set(prev.messages.map((m) => m.id));
        const fresh = incoming.filter((m) => !known.has(m.id));
        if (fresh.length === 0) return prev;
        const messages = [...prev.messages, ...fresh].sort((a, b) => a.id - b.id);
        lastIdRef.current = messages[messages.length - 1]?.id ?? lastIdRef.current;
        return { ...prev, messages };
      });
      requestAnimationFrame(scrollDown);
    },
    [scrollDown],
  );

  // Выбранный диалог: полная загрузка, затем дозагрузка нового по таймеру.
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    const controller = new AbortController();
    lastIdRef.current = 0;
    setThread(null);

    const tick = (first: boolean) => {
      fetchChat(selected, first ? 0 : lastIdRef.current, controller.signal).then((res) => {
        if (cancelled || !res.ok) return;
        if (first) {
          const messages = res.value.messages;
          lastIdRef.current = messages[messages.length - 1]?.id ?? 0;
          setThread({
            user: res.value.user,
            lang: res.value.lang,
            userAgent: res.value.userAgent,
            messages,
          });
          requestAnimationFrame(scrollDown);
          // Открытие диалога гасит его непрочитанные в списке слева.
          void reloadChats();
        } else {
          merge(res.value.messages);
        }
      });
    };

    tick(true);
    const timer = setInterval(() => tick(false), POLL_MS);
    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
    };
  }, [selected, merge, scrollDown, reloadChats]);

  // Поиск пользователя — с той же задержкой, что и в списке пользователей.
  useEffect(() => {
    if (!starting) return;
    const timer = setTimeout(() => {
      const q = query.trim();
      if (!q) {
        setFound([]);
        return;
      }
      void listUsers({ q, limit: 8 }).then((res) => {
        if (res.ok) setFound(res.value.users);
      });
    }, 250);
    return () => clearTimeout(timer);
  }, [starting, query]);

  async function handleSend() {
    const text = draft.trim();
    if (!text || !selected || sending) return;
    setSending(true);
    const res = await sendChatMessage(selected, text);
    setSending(false);
    if (res.ok) {
      setDraft('');
      merge([res.value.message]);
      void reloadChats();
    } else {
      setError(res.error);
    }
  }

  /** Включить или выключить пользователю вложения в чате. */
  async function handleFilesToggle(next: boolean) {
    if (!selected || !thread) return;
    const limits = { ...(thread.user.limits ?? {}) };
    if (next) limits.chatFiles = true;
    else delete limits.chatFiles;
    const res = await patchUser(selected, {
      limits: Object.keys(limits).length > 0 ? limits : null,
    });
    if (res.ok) {
      setThread((prev) => (prev ? { ...prev, user: res.value.user } : prev));
    } else {
      setError(res.error);
    }
  }

  /** Файл админа уходит сразу, вместе с текстом из поля, если он есть. */
  function handleFile(input: HTMLInputElement) {
    const picked = input.files?.[0];
    input.value = '';
    if (!picked || !selected || sending) return;
    if (picked.size > MAX_SUPPORT_FILE_BYTES || !SUPPORT_FILE_ACCEPT.includes(picked.type)) return;
    const reader = new FileReader();
    reader.onload = () => {
      void (async () => {
        setSending(true);
        const res = await sendChatMessage(selected, draft.trim(), {
          name: picked.name,
          dataUrl: String(reader.result),
        });
        setSending(false);
        if (res.ok) {
          setDraft('');
          merge([res.value.message]);
          void reloadChats();
        } else {
          setError(res.error);
        }
      })();
    };
    reader.readAsDataURL(picked);
  }

  /** Завершить разговор: у пользователя чистый лист, у админа — серая история. */
  async function handleClose() {
    if (!selected || !thread) return;
    if (!window.confirm(t('admin.chats.close.confirm', { email: thread.user.email }))) return;
    const res = await closeChat(selected);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    // Полная перезагрузка ветки: у всех сообщений появился archivedAt.
    const fresh = await fetchChat(selected);
    if (fresh.ok) {
      const messages = fresh.value.messages;
      lastIdRef.current = messages[messages.length - 1]?.id ?? 0;
      setThread({
        user: fresh.value.user,
        lang: fresh.value.lang,
        userAgent: fresh.value.userAgent,
        messages,
      });
    }
    await reloadChats();
  }

  async function handleDelete() {
    if (!selected || !thread) return;
    if (!window.confirm(t('admin.chats.clear.confirm', { email: thread.user.email }))) return;
    const res = await deleteChat(selected);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setSelected(null);
    setThread(null);
    await reloadChats();
  }

  function openChat(userId: string) {
    setStarting(false);
    setQuery('');
    setFound([]);
    setDraft('');
    setSelected(userId);
  }

  const format = (ms: number) =>
    new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(ms);

  const total = page?.total ?? 0;

  return (
    <div className={styles.panel}>
      {error && (
        <p className={styles.error} role="alert">
          {t(errorKey(error))}
        </p>
      )}

      <div className={styles.layout}>
        <aside className={styles.side}>
          <button
            type="button"
            className={`btn btn-sm ${styles.newChat}`}
            onClick={() => setStarting((v) => !v)}
          >
            {t('admin.chats.new')}
          </button>

          {starting && (
            <div className={styles.search}>
              <input
                className="input"
                type="search"
                value={query}
                autoFocus
                placeholder={t('admin.chats.search')}
                onChange={(e) => setQuery(e.target.value)}
              />
              {found.length > 0 && (
                <ul className={styles.found}>
                  {found.map((u) => (
                    <li key={u.id}>
                      <button type="button" className={styles.foundItem} onClick={() => openChat(u.id)}>
                        {u.email}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {page && page.chats.length === 0 && !starting ? (
            <p className={styles.empty}>{t('admin.chats.empty')}</p>
          ) : (
            <ul className={styles.chats}>
              {(page?.chats ?? []).map((chat: AdminChat) => (
                <li key={chat.userId}>
                  <button
                    type="button"
                    className={selected === chat.userId ? `${styles.chat} ${styles.chatActive}` : styles.chat}
                    onClick={() => openChat(chat.userId)}
                  >
                    <span className={styles.chatHead}>
                      <span className={styles.chatEmail}>
                        {chat.email || chat.userId}
                        {chat.lang && (
                          <span className={styles.langBadge} title={t('admin.chats.lang')}>
                            {chat.lang.toUpperCase()}
                          </span>
                        )}
                        {chat.userDeleted && <span className={styles.deleted}> · {t('admin.status.deleted')}</span>}
                      </span>
                      {chat.unread > 0 && <span className={styles.unread}>{chat.unread}</span>}
                    </span>
                    {chat.lastMessage && (
                      <span className={styles.preview}>
                        {chat.lastMessage.author === 'admin' ? '↩ ' : ''}
                        {chat.lastMessage.text.slice(0, 80)}
                      </span>
                    )}
                    {chat.lastMessage && (
                      <span className={styles.chatTime}>{format(chat.lastMessage.createdAt)}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {total > PAGE_SIZE && (
            <div className={styles.pager}>
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                {t('admin.prev')}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                {t('admin.next')}
              </button>
            </div>
          )}
        </aside>

        <section className={styles.threadPane}>
          {!selected ? (
            <p className={styles.placeholder}>{t('admin.chats.pick')}</p>
          ) : !thread ? (
            <p className={styles.placeholder}>…</p>
          ) : (
            <>
              <header className={styles.threadHead}>
                <span className={styles.threadUser}>
                  {thread.user.email}
                  {thread.user.name && <span className={styles.threadName}> · {thread.user.name}</span>}
                  {thread.lang && (
                    <span className={styles.langBadge} title={t('admin.chats.lang')}>
                      {thread.lang.toUpperCase()}
                    </span>
                  )}
                  {thread.userAgent && (
                    <span className={styles.langBadge} title={`${t('admin.chats.client')}: ${thread.userAgent}`}>
                      {shortClient(thread.userAgent) || t('admin.chats.client')}
                    </span>
                  )}
                </span>
                <span className={styles.threadActions}>
                  <label className={styles.filesToggle} title={t('admin.chats.files')}>
                    <input
                      type="checkbox"
                      checked={thread.user.limits?.chatFiles === true}
                      onChange={(e) => void handleFilesToggle(e.target.checked)}
                    />
                    <span>{t('admin.chats.files')}</span>
                  </label>
                  <button type="button" className="btn btn-sm" onClick={() => void handleClose()}>
                    {t('admin.chats.close')}
                  </button>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => void handleDelete()}>
                    {t('admin.chats.clear')}
                  </button>
                </span>
              </header>

              <div ref={listRef} className={styles.messages}>
                {thread.messages.length === 0 ? (
                  <p className={styles.placeholder}>{t('admin.chats.start')}</p>
                ) : (
                  thread.messages.map((m) => (
                    <div
                      key={m.id}
                      className={`${m.author === 'admin' ? styles.mine : styles.theirs}${
                        // Завершённая сессия: видна только здесь, серым.
                        m.archivedAt !== null ? ` ${styles.archived}` : ''
                      }`}
                    >
                      {m.file &&
                        (m.file.mime.startsWith('image/') ? (
                          <img
                            className={styles.image}
                            src={`/api/support/files/${m.id}`}
                            alt={m.file.name}
                          />
                        ) : (
                          <a
                            className={styles.fileLink}
                            href={`/api/support/files/${m.id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            📎 {m.file.name}
                          </a>
                        ))}
                      {m.text && <p className={styles.text}>{m.text}</p>}
                      <span className={styles.meta}>{format(m.createdAt)}</span>
                    </div>
                  ))
                )}
              </div>

              <div className={styles.composer}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={SUPPORT_FILE_ACCEPT}
                  className="visually-hidden"
                  tabIndex={-1}
                  onChange={(e) => handleFile(e.currentTarget)}
                />
                <button
                  type="button"
                  className={`btn btn-sm ${styles.attach}`}
                  aria-label={t('support.attach')}
                  disabled={sending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  📎
                </button>
                <textarea
                  className={`textarea ${styles.input}`}
                  rows={2}
                  value={draft}
                  maxLength={MAX_SUPPORT_TEXT}
                  placeholder={t('admin.chats.placeholder')}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void handleSend();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={sending || draft.trim() === ''}
                  onClick={() => void handleSend()}
                >
                  {t('admin.chats.send')}
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
